import { useMemo, useEffect } from 'react';
import { ColDef, ValueGetterParams, ValueFormatterParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import * as Sentry from '@sentry/react';
import { reconConfigMapAtom } from '../../state/FinanceStateJotai';
import { ReconConfigDataModel } from '../../../../schemas/ReconConfig.schema';
import { MetricsReconDataModel, MetricsReconSchema } from '../../../../schemas/MetricsRecon.schema';
import { useSchemaToGrid } from '../../../../util/schema-utils';
import { FMT, StandardFormatterId } from '../../../../util/formatter-service';
import { withOverrides } from '../../../../util/ag-grid-utils';

/**
 * Core hook that builds metrics recon column definitions from a reconConfigMap.
 * Does NOT access any Jotai atoms — safe to use outside Finance2 pages.
 */
export function useMetricsReconColDefsWithMap(reconConfigMap: Map<number, ReconConfigDataModel>) {
  const schema = useMemo(() => MetricsReconSchema.gridSchema(), []);

  // Debug: Check if reconConfigMap is ready
  useEffect(() => {
    if (reconConfigMap.size === 0) {
      Sentry.logger.warn('reconConfigMap is empty - this might cause formatting issues');
    }
  }, [reconConfigMap]);

  const schemaToColDefs = useSchemaToGrid();

  const columnDefs = useMemo(() => {
    // Return empty array if reconConfigMap is not ready
    if (reconConfigMap.size === 0) {
      return [];
    }

    const defs = schemaToColDefs(schema, [
      'companyId',
      'fundId',
      'foresightValue',
      'soiValue',
    ]) as ColDef<MetricsReconDataModel>[];

    const commonOverrides: ColDef<MetricsReconDataModel> = {
      filter: 'agNumberColumnFilter',
      valueFormatter: (params: ValueFormatterParams<MetricsReconDataModel>) => {
        const { data, value } = params;
        if (!data?.reconConfigId || value === null || value === undefined) {
          return '—';
        }
        const config = reconConfigMap.get(data.reconConfigId);
        if (!config) {
          Sentry.logger.warn('No config found for reconConfigId', { reconConfigId: data.reconConfigId });
          return String(value);
        }
        return FMT.format((config.formatter as StandardFormatterId) ?? 'numeric', value);
      },
      sortable: false,
    };

    const overrides: Record<string, ColDef<MetricsReconDataModel>> = {
      ['foresightValue']: { ...commonOverrides },
      ['soiValue']: {
        ...commonOverrides,
      },
    };

    const result = withOverrides(defs, overrides);

    // Add Field column (first)
    const fieldColumn = {
      headerName: 'Field',
      valueGetter: (params: ValueGetterParams<MetricsReconDataModel>) => {
        const { data } = params;
        if (!data?.reconConfigId) {
          return '';
        }
        const config = reconConfigMap.get(data.reconConfigId);
        if (!config) {
          Sentry.logger.warn('No config found for reconConfigId in Field column', {
            reconConfigId: data.reconConfigId,
          });
          return `Field ${data.reconConfigId}`;
        }
        return config.displayName ?? 'Unknown field';
      },
    };

    // Add Company Name column (second) - shows SOI company name if available
    const companyNameColumn = {
      headerName: 'Company Name',
      valueGetter: (params: ValueGetterParams<MetricsReconDataModel>) => {
        // Show the SOI company name if available, otherwise fall back to company name
        return params.data?.soiCompanyName || params.data?.companyName || '';
      },
    };

    // Add Status column - shows reconciliation status
    const statusColumn = {
      headerName: 'Status',
      valueGetter: (params: ValueGetterParams<MetricsReconDataModel>) => {
        return params.data?.status || '';
      },
      width: 140,
      cellStyle: (params: { value: string }) => {
        const status = params.value || '';
        const statusColors: Record<string, string> = {
          Match: '#28a745',
          'Close Match': '#ffc107',
          Variance: '#dc3545',
          'Missing Company': '#6c757d',
          'No Foresight Data': '#17a2b8',
        };
        const color = statusColors[status] || '#6c757d';
        return { color, fontWeight: '500' };
      },
    };

    result.unshift(companyNameColumn);

    // Add Field column first
    result.unshift(fieldColumn);

    // Add Status column last (far right)
    result.push(statusColumn);

    return result as ColDef<MetricsReconDataModel>[];
  }, [schemaToColDefs, schema, reconConfigMap]);

  return { columnDefs };
}

/**
 * Convenience wrapper that reads reconConfigMap from the Jotai atom.
 * Use on Finance2 pages where the atom is already provided.
 * Will trigger Suspense on first render since reconConfigMapAtom is async.
 */
export function useMetricsReconColDefs() {
  const jotaiReconConfigMap = useAtomValue(reconConfigMapAtom);
  return useMetricsReconColDefsWithMap(jotaiReconConfigMap);
}
